<template>
  <div v-if="show" class="app-modal" role="presentation">
    <div class="app-modal__backdrop" @click="emitClose"></div>

    <section
      class="app-modal__dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="app-modal-title"
      @click.stop
    >
      <header class="app-modal__header">
        <div>
          <h3 id="app-modal-title">{{ modalTitle }}</h3>
          <p>{{ modalSubtitle }}</p>
        </div>
        <button
          class="app-modal__close"
          type="button"
          :aria-label="text.actions.modal.close"
          @click="emitClose"
        >
          <i class="fa fa-times" aria-hidden="true"></i>
        </button>
      </header>

      <div class="app-modal__body">
        <aside class="app-modal__menu" :aria-label="text.actions.modal.sectionMenuAriaLabel">
          <button
            v-for="section in sections"
            :key="section.key"
            class="app-modal__menu-item"
            :class="{ active: activeSectionKey === section.key }"
            type="button"
            @click="activeSectionKey = section.key"
          >
            <span class="app-modal__menu-icon">
              <i class="fa" :class="section.icon" aria-hidden="true"></i>
            </span>
            <span class="app-modal__menu-copy">
              <strong>{{ section.title }}</strong>
              <small>{{ section.description }}</small>
            </span>
          </button>
        </aside>

        <div class="app-modal__content">
          <form class="app-modal__form" @submit.prevent="submitForm">
            <CustomerSectionGeneral
              :active="activeSectionKey === 'general'"
              :form="form"
              :v$="v$"
              :is-edit-mode="isEditMode"
              :existing-logo-url="existingLogoUrl"
              :logo-preview-url="logoPreviewUrl"
              :logo-marked-for-delete="logoMarkedForDelete"
              :logo-file-name="logoFileName"
              @logo-change="handleLogoChange"
              @logo-remove="handleLogoRemove"
            />

            <CustomerSectionContact
              :active="activeSectionKey === 'contact'"
              :form="form"
              :v$="v$"
            />

            <CustomerSectionClassification
              :active="activeSectionKey === 'classification'"
              :form="form"
              :customer-type-options="customerTypeOptions"
              :size-options="sizeOptions"
              :sector-options="sectorOptions"
              :origin-options="originOptions"
              :area-options="areaOptions"
              :can-assign-area="canAssignArea()"
            />

            <CustomerSectionAddress
              :active="activeSectionKey === 'address'"
              :form="form"
              :show-billing-address-toggle="showBillingAddressToggle"
              :country-options="countryOptions"
              :state-options="stateOptions"
            />

            <CustomerSectionFinancial
              :active="activeSectionKey === 'financial'"
              :form="form"
            />

            <footer class="app-modal__footer">
              <div class="app-modal__footer-left">
                <button
                  v-if="isEditMode"
                  type="button"
                  class="app-table-icon-btn app-table-icon-btn--delete customer-modal__delete-btn"
                  :title="text.actions.modal.deleteCustomer"
                  :aria-label="text.actions.modal.deleteCustomer"
                  @click="emitDelete"
                >
                  <i class="fa fa-trash" aria-hidden="true"></i>
                </button>
              </div>
              <div class="app-modal__footer-right">
                <button type="button" class="btn btn-light" @click="emitClose">{{ text.actions.common.cancel }}</button>
                <button type="submit" class="btn btn-primary">{{ submitButtonLabel }}</button>
              </div>
            </footer>
          </form>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import CustomerSectionGeneral from '@/components/customers/modal/CustomerSections/CustomerSectionGeneral.vue';
import CustomerSectionContact from '@/components/customers/modal/CustomerSections/CustomerSectionContact.vue';
import CustomerSectionClassification from '@/components/customers/modal/CustomerSections/CustomerSectionClassification.vue';
import CustomerSectionAddress from '@/components/customers/modal/CustomerSections/CustomerSectionAddress.vue';
import CustomerSectionFinancial from '@/components/customers/modal/CustomerSections/CustomerSectionFinancial.vue';

import customerCatalogsService from '@/services/customerCatalogs';
import generalCatalogsService from '@/services/generalCatalogs';
import areasService from '@/services/areas';
import {
  createInitialCustomerForm,
  buildCustomerFormFromEntity,
  buildCustomerCreatePayload,
} from '@/composables/customers/useCustomerCreateForm';
import { resolveOption } from '@/utils/optionValues';
import {
  createCustomerCatalogsState,
  fetchCustomerCatalogs,
} from '@/composables/customers/useCustomerCatalogs';
import { useVuelidate } from '@vuelidate/core';
import { email, helpers } from '@vuelidate/validators';
import { useDomainText } from '@/composables/text/useDomainText';

const { catalog: text } = useDomainText('customers');

export default {
  name: 'CustomerModal',
  components: {
    CustomerSectionGeneral,
    CustomerSectionContact,
    CustomerSectionClassification,
    CustomerSectionAddress,
    CustomerSectionFinancial,
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    mode: {
      type: String,
      default: 'create'
    },
    initialData: {
      type: Object,
      default: null
    },
    initialSectionKey: {
      type: String,
      default: 'general'
    }
  },
  emits: ['close', 'submit', 'delete'],
  data() {
    return {
      text,
      ...createCustomerCatalogsState(),
      sections: [
        {
          key: 'general',
          title: text.modal.sections.general.title,
          description: text.modal.sections.general.description,
          icon: 'fa-id-card-o'
        },
        {
          key: 'contact',
          title: text.modal.sections.contact.title,
          description: text.modal.sections.contact.description,
          icon: 'fa-address-book-o'
        },
        {
          key: 'classification',
          title: text.modal.sections.classification.title,
          description: text.modal.sections.classification.description,
          icon: 'fa-sitemap'
        },
        {
          key: 'address',
          title: text.modal.sections.address.title,
          description: text.modal.sections.address.description,
          icon: 'fa-map-marker'
        },
        {
          key: 'financial',
          title: text.modal.sections.financial.title,
          description: text.modal.sections.financial.description,
          icon: 'fa-bank'
        }
      ],
      activeSectionKey: 'general',
      form: createInitialCustomerForm(),
      logoFile: null,
      logoPreviewUrl: '',
      existingLogoUrl: '',
      logoMarkedForDelete: false,
    };
  },
  computed: {
    isEditMode() {
      return String(this.mode || 'create').toLowerCase() === 'edit';
    },
    modalTitle() {
      return this.isEditMode ? text.modal.editTitle : text.modal.createTitle;
    },
    modalSubtitle() {
      return this.isEditMode
        ? text.modal.editSubtitle
        : text.modal.createSubtitle;
    },
    submitButtonLabel() {
      return this.isEditMode ? text.modal.submitEdit : text.modal.submitCreate;
    },
    logoFileName() {
      return this.logoFile instanceof File ? String(this.logoFile.name || '') : '';
    },
    showBillingAddressToggle() {
      if (!this.isEditMode) {
        return false;
      }

      return Number(this.initialData?.has_billing_data || 0) === 1;
    },
  },
  validations() {
    return {
      form: {
        name: {
          required: helpers.withMessage(
            text.modal.validation.nameRequired,
            (value) => helpers.req(String(value || '').trim())
          ),
        },
        email: {
          emailFormat: helpers.withMessage(
            text.modal.validation.emailInvalid,
            (value) => {
              const normalized = String(value || '').trim();

              if (!helpers.req(normalized)) {
                return true;
              }

              return email.$validator ? email.$validator(normalized) : email(normalized);
            }
          ),
        },
      },
    };
  },
  setup() {
    const v$ = useVuelidate();

    return { v$ };
  },
  watch: {
    show(isVisible) {
      if (isVisible) {
        this.handleModalOpen();
        return;
      }

      this.form = createInitialCustomerForm();
      this.activeSectionKey = this.sections[0].key;
      this.resetLogoState();
      this.v$.$reset();
    },
    initialData() {
      if (!this.show || !this.isEditMode) {
        return;
      }

      this.applyInitialDataToForm();
    }
  },
  methods: {
    resolveSectionKey(sectionKey) {
      const validKeys = this.sections.map((section) => section.key);
      const normalized = String(sectionKey || '').trim().toLowerCase();

      if (normalized === 'comments') {
        return 'general';
      }

      return validKeys.includes(normalized) ? normalized : this.sections[0].key;
    },
    applyInitialDataToForm() {
      const formFromEntity = buildCustomerFormFromEntity(this.initialData || {});

      this.form = {
        ...createInitialCustomerForm(),
        ...formFromEntity,
        customerType: resolveOption(this.customerTypeOptions, formFromEntity.customerType),
        size: resolveOption(this.sizeOptions, formFromEntity.size),
        sector: resolveOption(this.sectorOptions, formFromEntity.sector),
        source: resolveOption(this.originOptions, formFromEntity.source),
        area: resolveOption(this.areaOptions, formFromEntity.area),
        country: resolveOption(this.countryOptions, formFromEntity.country),
        state: resolveOption(this.stateOptions, formFromEntity.state),
        status: String(formFromEntity.status || 'Activo'),
        useBillingAddress: String(formFromEntity.useBillingAddress || 'No'),
      };

      const currentLogoUrl = String(this.initialData?.logo_thumb_url || this.initialData?.logo_url || '').trim();
      this.existingLogoUrl = currentLogoUrl;
      this.logoMarkedForDelete = false;
    },
    async handleModalOpen() {
      await this.ensureCatalogsLoaded();
      this.activeSectionKey = this.resolveSectionKey(this.initialSectionKey);
      this.resetLogoState();

      if (this.isEditMode) {
        this.applyInitialDataToForm();
      } else {
        this.form = createInitialCustomerForm();
      }

      this.v$.$reset();
    },
    async ensureCatalogsLoaded() {
      if (this.catalogsLoaded || this.catalogsLoading) {
        return;
      }

      this.catalogsLoading = true;
      try {
        const catalogs = await fetchCustomerCatalogs({
          canAssignArea: this.canAssignArea(),
          customerCatalogsService,
          generalCatalogsService,
          areasService,
        });

        Object.assign(this, catalogs);
      } catch (error) {
        Object.assign(this, createCustomerCatalogsState());
      } finally {
        this.catalogsLoading = false;
      }
    },
    canAssignArea() {
      return this.$can('customers.areas.assign');
    },
    emitClose() {
      this.$emit('close');
    },
    emitDelete() {
      this.$emit('delete', this.initialData || null);
    },
    revokeLogoPreview() {
      if (!this.logoPreviewUrl) {
        return;
      }

      URL.revokeObjectURL(this.logoPreviewUrl);
      this.logoPreviewUrl = '';
    },
    resetLogoState() {
      this.revokeLogoPreview();
      this.logoFile = null;
      this.existingLogoUrl = '';
      this.logoMarkedForDelete = false;
    },
    handleLogoChange(file) {
      const nextFile = file instanceof File ? file : null;

      this.revokeLogoPreview();
      this.logoFile = nextFile;

      if (!nextFile) {
        return;
      }

      this.logoPreviewUrl = URL.createObjectURL(nextFile);
      this.logoMarkedForDelete = false;
    },
    handleLogoRemove() {
      if (this.logoFile instanceof File) {
        this.logoFile = null;
        this.revokeLogoPreview();
        return;
      }

      if (!this.isEditMode) {
        return;
      }

      this.logoMarkedForDelete = true;
    },
    submitForm() {
      this.v$.$touch();

      if (this.v$.$invalid) {
        if (this.v$.form.name.$error) {
          this.activeSectionKey = 'general';
        } else if (this.v$.form.email.$error) {
          this.activeSectionKey = 'contact';
        }

        return;
      }

      const payload = buildCustomerCreatePayload(this.form);

      if (this.logoFile instanceof File) {
        payload.logo = this.logoFile;
      }

      if (this.isEditMode && this.logoMarkedForDelete && !(this.logoFile instanceof File)) {
        payload.delete_logo = 1;
      }

      this.$emit('submit', payload);
    }
  },
  beforeUnmount() {
    this.revokeLogoPreview();
  }
};
</script>

<style scoped>
.app-modal__footer-left {
  margin-right: auto;
}

.customer-modal__delete-btn {
  min-height: 38px;
  height: 38px;
  width: 44px;
  min-width: 44px;
  padding: 0 !important;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
</style>
